import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import { API_ROUTES } from "../../api/ApiRoutes";
import createPrivateAdminPanelInstance from "../../api/PrivateAdminPanelInstance";
import { toQueryString } from "../../components/shared/table-filters/filterQueryUtils";
import type {
  CasinoBetsResponse,
  GetUserCasinoBetsParams,
} from "../../types/bets";

type StoredToken = { AccessToken?: string };

const DEFAULT_RESPONSE: CasinoBetsResponse = {
  items: [],
  pagination: {
    current_page: 1,
    page_size: 20,
    total_results: 0,
    total_pages: 0,
    has_next: false,
    has_prev: false,
  },
};

const fetchUserCasinoBets = async (
  params: GetUserCasinoBetsParams,
): Promise<CasinoBetsResponse> => {
  const tokenString = localStorage.getItem("token");
  const token: StoredToken | null = tokenString ? JSON.parse(tokenString) : null;
  const instance = createPrivateAdminPanelInstance(token?.AccessToken);

  const queryParams: Record<string, unknown> = {
    type: "casino",
    page: params.page,
    start_date: params.start_date,
    end_date: params.end_date,
  };

  if (params.page_size !== undefined && params.page_size !== null) {
    queryParams.page_size = params.page_size;
  }

  if (params.sort_by && params.sort_order) {
    queryParams.sort_by = params.sort_by;
    queryParams.sort_order = params.sort_order;
  }

  if (params.totals) {
    queryParams.totals = params.totals;
  }

  if (
    params.last_evaluated_key !== undefined &&
    params.last_evaluated_key !== null &&
    params.last_evaluated_key !== ""
  ) {
    queryParams.last_evaluated_key = params.last_evaluated_key;
  }

  if (Array.isArray(params.status)) {
    if (params.status.length > 0) {
      queryParams.status = params.status.join(",");
    }
  } else if (params.status) {
    queryParams.status = params.status;
  }
  if (params.money_type) {
    queryParams.money_type = params.money_type;
  }
  if (params.provider) {
    queryParams.provider = params.provider;
  }
  if (params.game_id) {
    queryParams.game_id = params.game_id;
  }

  const response = await instance.get(
    API_ROUTES.GET_USER_CASINO_BETS.replace("{user_id}", params.user_id) +
      toQueryString(queryParams),
  );

  const body = response?.data?.data;
  if (!body || typeof body !== "object") {
    return DEFAULT_RESPONSE;
  }
  return body as CasinoBetsResponse;
};

const serializeCursor = (
  key: GetUserCasinoBetsParams["last_evaluated_key"],
): string => {
  if (key === undefined || key === null) return "";
  if (typeof key === "string") return key;
  return JSON.stringify(key);
};

export const useGetUserCasinoBets = (
  params: GetUserCasinoBetsParams | null,
): UseQueryResult<CasinoBetsResponse, Error> => {
  return useQuery<CasinoBetsResponse, Error>({
    queryKey: [
      "GET_USER_CASINO_BETS",
      params?.user_id ?? "",
      params?.page ?? 1,
      params?.page_size ?? "",
      params?.start_date ?? "",
      params?.end_date ?? "",
      serializeCursor(params?.last_evaluated_key),
      params?.totals ?? "",
      params?.sort_by ?? "",
      params?.sort_order ?? "",
      Array.isArray(params?.status)
        ? params.status.join(",")
        : (params?.status ?? ""),
      params?.money_type ?? "",
      params?.provider ?? "",
      params?.game_id ?? "",
    ],
    queryFn: () => fetchUserCasinoBets(params as GetUserCasinoBetsParams),
    enabled: Boolean(params?.user_id),
    placeholderData: (previousData) => previousData,
    staleTime: 1000 * 60,
    refetchOnWindowFocus: false,
  });
};
